import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { useEffect, useMemo, useRef, useState } from "react";

const BLUE = "#0A7CFF";
const ORANGE = "#F97316";
const INK = "#0F172A";

function roundedGeometry(w, h, r, depth) {
  const s = new THREE.Shape();
  const x = -w / 2, y = -h / 2;
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y);
  s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + h - r);
  s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  s.lineTo(x + r, y + h);
  s.quadraticCurveTo(x, y + h, x, y + h - r);
  s.lineTo(x, y + r);
  s.quadraticCurveTo(x, y, x + r, y);
  const g = new THREE.ExtrudeGeometry(s, { depth, bevelEnabled: true, bevelThickness: 0.025, bevelSize: 0.025, bevelSegments: 3, curveSegments: 10 });
  g.center();
  return g;
}

const Float = ({ children, position, rotation = [0, 0, 0], speed = 1, amp = 0.16, offset = 0 }) => {
  const ref = useRef();
  useFrame(({ clock }) => {
    const t = clock.elapsedTime * speed + offset;
    ref.current.position.y = position[1] + Math.sin(t) * amp;
    ref.current.rotation.x = rotation[0] + Math.sin(t * 0.7) * 0.07;
    ref.current.rotation.y = rotation[1] + Math.cos(t * 0.5) * 0.09;
    ref.current.rotation.z = rotation[2];
  });
  return <group ref={ref} position={position} rotation={rotation}>{children}</group>;
};

const LandingCard = ({ geo, scale = 1 }) => (
  <group scale={scale}>
    <mesh geometry={geo}><meshStandardMaterial color="#ffffff" roughness={0.4} metalness={0.02} /></mesh>
    <mesh position={[-0.28, 0.62, 0.06]}><boxGeometry args={[0.6, 0.09, 0.03]} /><meshStandardMaterial color={INK} roughness={0.5} /></mesh>
    <mesh position={[-0.18, 0.44, 0.06]}><boxGeometry args={[0.8, 0.06, 0.03]} /><meshStandardMaterial color="#cbd5e1" roughness={0.6} /></mesh>
    <mesh position={[-0.28, 0.31, 0.06]}><boxGeometry args={[0.6, 0.06, 0.03]} /><meshStandardMaterial color="#cbd5e1" roughness={0.6} /></mesh>
    <mesh position={[-0.3, 0.05, 0.07]}><boxGeometry args={[0.55, 0.16, 0.04]} /><meshStandardMaterial color={BLUE} roughness={0.35} /></mesh>
    <mesh position={[0, -0.55, 0.06]}><boxGeometry args={[1.15, 0.7, 0.03]} /><meshStandardMaterial color="#e2e8f0" roughness={0.7} /></mesh>
  </group>
);

const Rig = ({ scroll, mouse, children }) => {
  const ref = useRef();
  useFrame((_, dt) => {
    const s = scroll?.get?.() ?? 0;
    const m = mouse.current;
    const d = Math.min(dt, 0.05);
    ref.current.rotation.x = THREE.MathUtils.damp(ref.current.rotation.x, -m.y * 0.16 + s * 0.5, 3.5, d);
    ref.current.rotation.y = THREE.MathUtils.damp(ref.current.rotation.y, m.x * 0.28 + s * 1.4, 3.5, d);
    ref.current.position.y = THREE.MathUtils.damp(ref.current.position.y, -s * 3.2, 3.5, d);
    ref.current.position.x = THREE.MathUtils.damp(ref.current.position.x, m.x * 0.25, 3.5, d);
  });
  return <group ref={ref}>{children}</group>;
};

const Scene = ({ scroll, mouse }) => {
  const page = useMemo(() => roundedGeometry(1.5, 2, 0.22, 0.1), []);
  const card = useMemo(() => roundedGeometry(1.4, 1.9, 0.14, 0.06), []);
  const vw = useThree((s) => s.viewport.width);
  const edge = Math.max(5.2, vw * 0.62);
  return (
    <>
      <ambientLight intensity={1.1} />
      <directionalLight position={[6, 8, 6]} intensity={2.2} />
      <directionalLight position={[-6, -2, 4]} intensity={0.6} color="#dbeafe" />
      <pointLight position={[-4, -3, 3]} intensity={30} color={ORANGE} distance={12} />
      <Rig scroll={scroll} mouse={mouse}>
        <group position={[3.1, 0.1, 0]} rotation={[0.05, -0.35, 0]}>
          <Float position={[-0.75, -0.05, -0.55]} rotation={[0, 0, -0.42]} speed={0.9} amp={0.12}>
            <mesh geometry={page}><meshStandardMaterial color={BLUE} roughness={0.32} metalness={0.05} /></mesh>
          </Float>
          <Float position={[-0.35, 0.05, -0.25]} rotation={[0, 0, -0.2]} speed={0.9} amp={0.12} offset={0.6}>
            <mesh geometry={page}><meshStandardMaterial color={BLUE} roughness={0.32} metalness={0.05} /></mesh>
          </Float>
          <Float position={[0.15, 0.1, 0.2]} rotation={[0, 0, 0.02]} speed={0.9} amp={0.12} offset={1.2}>
            <LandingCard geo={card} />
          </Float>
          <Float position={[1.55, 1.35, 0.9]} speed={1.4} amp={0.18} offset={2}>
            <mesh><torusGeometry args={[0.32, 0.11, 24, 64]} /><meshStandardMaterial color={ORANGE} roughness={0.3} /></mesh>
          </Float>
          <Float position={[1.4, -1.5, 0.6]} speed={1.1} amp={0.14} offset={3}>
            <mesh><icosahedronGeometry args={[0.3, 0]} /><meshStandardMaterial color={INK} roughness={0.35} flatShading /></mesh>
          </Float>
        </group>
        <Float position={[-edge, 2.6, -3]} rotation={[0.1, 0.4, -0.15]} speed={0.7} amp={0.2} offset={1}>
          <LandingCard geo={card} scale={0.55} />
        </Float>
        <Float position={[-edge - 0.4, -2.6, -2]} speed={1.2} amp={0.16} offset={2.5}>
          <mesh><sphereGeometry args={[0.28, 32, 32]} /><meshStandardMaterial color={BLUE} roughness={0.25} /></mesh>
        </Float>
        <Float position={[0.9, 2.7, -2.5]} speed={1.3} amp={0.14} offset={4}>
          <mesh><torusGeometry args={[0.2, 0.07, 20, 48]} /><meshStandardMaterial color={ORANGE} roughness={0.3} /></mesh>
        </Float>
        <Float position={[5.6, -0.4, -2.5]} rotation={[0.2, -0.5, 0.2]} speed={0.8} amp={0.2} offset={5}>
          <LandingCard geo={card} scale={0.5} />
        </Float>
      </Rig>
    </>
  );
};

export const HeroScene = ({ scroll }) => {
  const mouse = useRef({ x: 0, y: 0 });
  const wrapRef = useRef(null);
  const [active, setActive] = useState(true);
  const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width:767px)").matches;
  useEffect(() => {
    const onMove = (e) => {
      mouse.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.current.y = (e.clientY / window.innerHeight) * 2 - 1;
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => window.removeEventListener("pointermove", onMove);
  }, []);
  // Pause the render loop when the hero leaves the viewport or the tab is hidden
  useEffect(() => {
    const el = wrapRef.current;
    let visible = true;
    let shown = !document.hidden;
    const update = () => setActive(visible && shown);
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; update(); }, { threshold: 0.01 });
    if (el) io.observe(el);
    const onVis = () => { shown = !document.hidden; update(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { io.disconnect(); document.removeEventListener("visibilitychange", onVis); };
  }, []);
  return (
    <div ref={wrapRef} data-testid="hero-3d-wrap" style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      <Canvas
        data-testid="hero-3d-canvas"
        frameloop={active ? "always" : "never"}
        dpr={isMobile ? [1, 1.25] : [1, 1.6]}
        camera={{ position: [0, 0, 9], fov: 38 }}
        gl={{ alpha: true, antialias: !isMobile, powerPreference: "high-performance" }}
        style={{ background: "transparent" }}
      >
        <Scene scroll={scroll} mouse={mouse} />
      </Canvas>
    </div>
  );
};
